import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatAngularModule } from '../../shared/modules/mat-angular.module';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService, RegisterRequest } from '../../services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorHandlerService } from '../../services/error-handler.service';
import { firstValueFrom } from 'rxjs';
import { CommonService } from '../../services/common.service';

@Component({
  selector: 'app-register',
  imports: [
    CommonModule,
    MatAngularModule,
    ReactiveFormsModule,
    RouterModule
  ],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
  standalone: true,
})
export class RegisterComponent {
  fb = inject(FormBuilder);
  router = inject(Router);
  authService = inject(AuthService);
  snackBar = inject(MatSnackBar);
  errorHandler = inject(ErrorHandlerService);
  commonService = inject(CommonService);

  registerForm: FormGroup = this.fb.group({
    first_name: ['', [Validators.required, Validators.minLength(2)]],
    last_name: ['', [Validators.required, Validators.minLength(2)]],
    email: ['', [Validators.required, Validators.email]],
    phone: ['', [Validators.pattern(/^\+?[\d\s\-\(\)]+$/)]], // Optional field
    user_type: ['Company/brand', Validators.required],
    business_title: ['NA'],
    password: ['', [Validators.required, Validators.minLength(8), this.passwordStrengthValidator]],
    confirm_password: ['', [Validators.required]]
  }, { validators: this.passwordMatchValidator });

  isLoading = false;
  showBusinessFields = true; // Both Company/brand and Agency require business_title

  constructor() {
    // Initialize business_title as required since default is Company/brand
    this.registerForm.get('business_title')?.setValidators([Validators.required]);
    this.registerForm.get('business_title')?.updateValueAndValidity();
    
    this.registerForm.get('user_type')?.valueChanges.subscribe(value => {
      // Both Company/brand and Agency require business_title
      this.showBusinessFields = true;
      this.registerForm.get('business_title')?.setValidators([Validators.required]);
      this.registerForm.get('business_title')?.updateValueAndValidity();
    });
  }

  passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.value;
    if (!password) {
      return null;
    }

    const errors: ValidationErrors = {};

    if (password.length < 8) {
      errors['minlength'] = { requiredLength: 8, actualLength: password.length };
    }
    if (!/[A-Z]/.test(password)) {
      errors['noUppercase'] = true;
    }
    if (!/[a-z]/.test(password)) {
      errors['noLowercase'] = true;
    }
    if (!/[0-9]/.test(password)) {
      errors['noDigit'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirm_password');

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }

    return null;
  }

  async register() {
    if (this.registerForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    this.isLoading = true;

    const request: RegisterRequest = {
      first_name: this.registerForm.value.first_name,
      last_name: this.registerForm.value.last_name,
      email: this.registerForm.value.email,
      phone: this.registerForm.value.phone,
      user_type: this.registerForm.value.user_type,
      business_title: this.registerForm.value.business_title || undefined,
      password: this.registerForm.value.password
    };

    try {
      await firstValueFrom(this.authService.register(request));
      this.snackBar.open('Registration successful! Welcome to AI Optima.', 'Close', {
        duration: 5000,
        panelClass: ['success-snackbar']
      });
      if (this.commonService.isGenericFlowActive()) {
        this.router.navigate(['/university/projects']);
      } else {
        this.router.navigate(['/dashboard']);
      }
    } catch (error: any) {
      const errorMessage = this.errorHandler.extractErrorMessage(
        error,
        'Registration failed. Please try again.'
      );

      this.snackBar.open(errorMessage, 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
    } finally {
      this.isLoading = false;
    }
  }

  private markFormGroupTouched() {
    Object.keys(this.registerForm.controls).forEach(key => {
      const control = this.registerForm.get(key);
      control?.markAsTouched();
    });
  }

  getErrorMessage(controlName: string): string {
    const control = this.registerForm.get(controlName);

    if (control?.hasError('required')) {
      return `${this.getFieldLabel(controlName)} is required`;
    }

    if (control?.hasError('email')) {
      return 'Please enter a valid email address';
    }

    if (control?.hasError('minlength')) {
      const requiredLength = control.errors?.['minlength'].requiredLength;
      return `${this.getFieldLabel(controlName)} must be at least ${requiredLength} characters`;
    }

    if (control?.hasError('pattern')) {
      if (controlName === 'phone') {
        return 'Please enter a valid phone number';
      }
    }

    if (control?.hasError('passwordMismatch')) {
      return 'Passwords do not match';
    }

    // Password strength validation errors
    if (controlName === 'password') {
      if (control?.hasError('noUppercase')) {
        return 'Password must contain at least one uppercase letter';
      }
      if (control?.hasError('noLowercase')) {
        return 'Password must contain at least one lowercase letter';
      }
      if (control?.hasError('noDigit')) {
        return 'Password must contain at least one digit';
      }
    }

    return '';
  }

  private getFieldLabel(controlName: string): string {
    const labels: { [key: string]: string } = {
      first_name: 'First name',
      last_name: 'Last name',
      email: 'Email',
      phone: 'Phone number',
      business_title: 'Business title',
      password: 'Password',
      confirm_password: 'Confirm password'
    };
    return labels[controlName] || controlName;
  }
}

